// Turn a raw platform dispatch error into ONE plain-English sentence for the UI.
//
// Adapters store the upstream API's raw error — often a JSON blob with codes,
// log ids and doc links — in the DispatchResult. Rendering that verbatim dumps
// "long code stuff" at the user. This maps the errors we recognise to friendly
// copy and, for everything else, pulls the human-readable message out of the
// JSON (or trims the raw string) so a failed leg reads like a sentence.

/** Known error signatures → friendly copy. Matched as a substring, case-insensitive.
 * Order matters: the first pattern that matches wins, so the more specific
 * signatures are listed before the broad catch-alls. */
const KNOWN: { match: RegExp; message: string }[] = [
  {
    match: /unaudited_client_can_only_post_to_private_accounts/i,
    message: "TikTok posting isn't available yet.",
  },
  {
    // You're out of credits (server 402 / spend-credits guard). The UI pairs
    // this with a top-up CTA — see isInsufficientCreditsError.
    match: /insufficient[_ ]?credits|out of credits|no credits|payment required|\b402\b/i,
    message: "You're out of publish credits. Buy more credits to keep publishing.",
  },
  {
    // YouTube's shared daily upload quota is exhausted (uploadLimitExceeded /
    // quotaExceeded) — a wait, not a fixable input.
    match: /uploadlimitexceeded|quota\s*exceeded|dailylimitexceeded|exceeded.*daily|youtubesignuprequired/i,
    message: "YouTube's daily upload limit was reached — it'll publish once the limit resets.",
  },
  {
    // X (Twitter) endpoints that require a paid API tier (453 / access level).
    match: /\b453\b|paid\s*(api\s*)?tier|elevated access|not permitted.*access level|api\s*plan/i,
    message: "Posting to X needs a paid X API tier on this account.",
  },
  {
    // The media is too big/long for the platform (413, or a duration/size cap).
    match: /\b413\b|payload too large|file too large|too large|video is too long|exceeds.*(size|duration)|duration.*exceed/i,
    message: "The media file is too large or too long for this platform — trim or compress it and retry.",
  },
  {
    // The platform is throttling us (429 / rate limit) — a retry, not a fix.
    match: /\b429\b|rate limit|too many requests|try again later/i,
    message: "The platform is busy right now — wait a moment and try again.",
  },
  {
    match: /missing media field|invalid[- ]request/i,
    message: "The platform rejected the upload. Try again, or re-connect the account.",
  },
  {
    // A revoked/expired token — the account must be reconnected before it will
    // publish again.
    match: /\b401\b|unauthor(i[sz]ed|ised)|invalid[_ ]?(grant|token)|token.*(expired|revoked)|reauth/i,
    message: "This account needs to be reconnected before it can publish again.",
  },
];

/** True when a dispatch error is an out-of-credits failure, so the caller can
 * pair the humanized message with a "Buy credits" top-up CTA. */
export function isInsufficientCreditsError(error: string | undefined | null): boolean {
  if (!error) return false;
  return /insufficient[_ ]?credits|out of credits|no credits|payment required|\b402\b/i.test(error);
}

/**
 * True when a leg didn't publish for a reason that is NOT the user's fault and
 * needs no action from them — a platform we know is still pending approval on
 * our side (TikTok's Content Posting audit today). The UI renders these as a
 * calm "held" state rather than a red error, so an expected, temporary hold
 * never looks like something the user broke or must fix.
 */
export function isPendingPlatformError(error: string | undefined | null): boolean {
  if (!error) return false;
  return /unaudited_client_can_only_post_to_private_accounts/i.test(error);
}

/** Pull the most human field out of a JSON error blob, if it is one. */
function messageFromJson(raw: string): string | null {
  try {
    const p = JSON.parse(raw) as Record<string, unknown> & {
      error?: unknown;
      errors?: Array<{ message?: string }>;
    };
    const err = p.error as { message?: string } | string | undefined;
    const m =
      (typeof err === "object" && err?.message) ||
      (Array.isArray(p.errors) && p.errors[0]?.message) ||
      (p.message as string | undefined) ||
      (p.detail as string | undefined) ||
      (typeof err === "string" ? err : undefined);
    return typeof m === "string" ? m : null;
  } catch {
    return null;
  }
}

const MAX = 160;

/**
 * A short, human sentence for a failed dispatch leg. Never returns raw JSON or a
 * URL, and never runs long — the panel gets a status line, not a stack trace.
 */
export function humanizeDispatchError(error: string | undefined | null): string {
  if (!error || !error.trim()) return "Failed.";
  for (const k of KNOWN) if (k.match.test(error)) return k.message;

  // Adapter errors are often "some prefix: {json}"; isolate the JSON if present.
  const braceAt = error.indexOf("{");
  const human = braceAt >= 0 ? messageFromJson(error.slice(braceAt)) : null;

  let out = (human ?? error)
    .replace(/https?:\/\/\S+/g, "") // a doc link is noise in a status line
    .replace(/[{}"]/g, " ") // drop any stray JSON punctuation
    .replace(/\s+/g, " ")
    .trim();
  if (out.length > MAX) out = `${out.slice(0, MAX - 1).trimEnd()}…`;
  return out || "Failed.";
}
